import React, {
  useState,
  useContext,
  useEffect,
  useRef,
} from 'react';
import { withStyles } from '@material-ui/core/styles';
import { I18nContext } from 'translations';
import {
  Table,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  TableContainer,
  TablePagination,
  Checkbox,
  Tooltip,
  IconButton,
} from '@material-ui/core';
import EmptyData from '../EmptyData';
import styles from '../style';
import SalesAdapter from 'adapters/SalesAdapter';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowDropUpIcon from '@material-ui/icons/ArrowDropUp';
import SaleRow from './SaleRow';
import { modalMessage } from 'utils';
import { CANCELED_BOOKING_STATUS_CODES } from 'utils/constants';

// Orden con un booking anterior cancelado/expirado: sigue siendo
// seleccionable (ver MercadoFlexSale#isReadyToDispatch), pero al tildarla se
// va a crear un envio nuevo en vez de reactivar el anterior.
const wasCanceledAndRelaunchable = (sale) =>
  ![null, undefined].includes(sale?.booking_id) &&
  CANCELED_BOOKING_STATUS_CODES.includes(sale?.status_cd);

const SalesTable = ({
  classes,
  handleChangeCheck,
  data,
  recordsPerPage,
  getSales,
  filters,
  closeCreatePiboxButton,
  order,
  setOrder
}) => {
  const { translate } = useContext(I18nContext);
  const [currentPage, setCurrentPage] = useState(data.page);
  const [citySelected, setCitySelected] = useState(null);
  const [allChecked, setAllChecked] = useState(false);
  const [checkboxStates, setCheckboxStates] = useState({});
  const [expandedRows, setExpandedRows] = useState({});

  const prevState = useRef();

  useEffect(() => {
    const currentSelected =
      Object.values(checkboxStates)?.filter((e) => e === true) || false;

    if (currentSelected) {
      if (currentSelected < 2 && currentSelected < prevState) {
        setCitySelected(null);
      } else {
        setCitySelected(checkboxStates?.sale?.order?.city_id);
      }
      prevState.current =
        Object.values(checkboxStates)?.filter((e) => e === true) || false;
    }
  }, [checkboxStates]);

  const applyCheck = (name, checked, sale) => {
    setCheckboxStates({ ...checkboxStates, [name]: checked, sale });
    handleChangeCheck(
      { ...checkboxStates, [name]: checked },
      { ...sale, checked }
    );
  };

  const handleChange = async (event, sale) => {
    const { name, checked } = event.target;

    if (checked && wasCanceledAndRelaunchable(sale)) {
      const confirmation = await modalMessage(
        translate('app.sales.relaunch.confirmTitle'),
        translate('app.sales.relaunch.warning'),
        'warning',
        translate('accept'),
        true,
        true,
        translate('cancel')
      );
      if (!confirmation.isConfirmed) return;
    }

    applyCheck(name, checked, sale);
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
    getSales({page:newPage});
  };

  useEffect(() => {
    setCurrentPage(data.page);
  }, [data]);

  const handleAllCheckBoxChange = (e) => {
    const newCheckedState = !allChecked;
    setAllChecked(newCheckedState);

    const updatedCheckboxStates = data.data.reduce((acc, sale, index) => {
      acc[index] = newCheckedState;
      return acc;
    }, {});

    setCheckboxStates(updatedCheckboxStates);

    // Llamar a handleChangeCheck para cada venta
    data.data.forEach((saleObject, index) => {
      const sale = SalesAdapter(saleObject);
      handleChangeCheck(
        { ...checkboxStates, [index]: newCheckedState },
        { ...sale, checked: newCheckedState }
      );
    });

    if (newCheckedState === false) {
      closeCreatePiboxButton();
    }
  };

  const handleChangeOrder = () => {
    const newOrder = order === 'desc' ? 'asc' : 'desc';
    setOrder(newOrder);
  }

  const toggleExpandRow = (index) => {
    setExpandedRows((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const renderAllCheckBox = () => {
    return filters?.city_id ? (
      <Checkbox
        checked={allChecked}
        onChange={(e) => handleAllCheckBoxChange(e)}
        name="all"
        color="primary"
        size="small"
        style={{ padding: 2 }}
      />
    ) : (
      <Tooltip
        title={translate('app.sales.checkAll')}
        aria-label={translate('app.sales.checkAll')}
      >
        <CheckBoxOutlineBlankIcon />
      </Tooltip>
    );
  };

  if (!data || (data && data.data.length <= 0)) {
    return <EmptyData message={translate('thereIsNoData')} />;
  }

  const sales = (data.data || []).map((saleObject) => SalesAdapter(saleObject));

  // Guard transicional (docs/2026-09-01-mercadoflex-pack-grouping-frontend.md,
  // R4/F3): mientras exista data sin agrupar (flag OFF o historicos), dos
  // filas pueden compartir shipping_id. Seleccionar una debe deshabilitar
  // las demas del mismo envio para que no se despache dos veces.
  const selectedShippingIds = new Set(
    Object.entries(checkboxStates)
      .filter(([key, checked]) => key !== 'sale' && checked)
      .map(([key]) => sales[key]?.shipping_id)
      .filter(Boolean)
  );

  return (
    <>
      <TableContainer>
        <Table className={classes.table} size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>
               {renderAllCheckBox()}
              </TableCell>
              <TableCell>{translate('app.sales.table.status')}</TableCell>
              <TableCell>{translate('app.sales.table.idOrder')}</TableCell>
              <TableCell>{translate('app.sales.table.trackingNumber')}</TableCell>
              <TableCell>{translate('app.sales.table.product')}</TableCell>
              <TableCell>
                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                  {translate('app.sales.table.date')}
                  <IconButton aria-label="order_direction" size='small' onClick={handleChangeOrder}>
                    {
                      order === 'desc'
                      ? <ArrowDropUpIcon />
                      : <ArrowDropDownIcon />
                    }
                  </IconButton>
                </span>
              </TableCell>
              <TableCell>{translate('app.sales.table.shipping_date')}</TableCell>
              <TableCell>{translate('app.sales.table.integration')}</TableCell>
              <TableCell>{translate('app.sales.table.city')}</TableCell>
              <TableCell>
                {translate('app.sales.table.deliveryStatus')}
              </TableCell>
              <TableCell>{translate('app.sales.table.shipmentId')}</TableCell>
              <TableCell>
                {translate('app.sales.table.deliveryAddress')}
              </TableCell>
              <TableCell>
                {translate('app.sales.table.deliveryNumber')}
              </TableCell>
              <TableCell>
                {translate('app.sales.table.quantity')}
              </TableCell>
              <TableCell>
                {translate('app.sales.table.orderType')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sales.map((sale, index) => {
              const sameShipmentSelected =
                !checkboxStates[index] &&
                sale.shipping_id &&
                selectedShippingIds.has(sale.shipping_id);

              return (
                <SaleRow
                  key={`${sale.id}${index}`}
                  sale={sale}
                  index={index}
                  classes={classes}
                  translate={translate}
                  citySelected={citySelected}
                  checked={checkboxStates[index] || false}
                  disabledCheckbox={
                    sale.destination?.lat === null ||
                    sale.destination?.address === null ||
                    (citySelected && citySelected !== sale?.order?.city_id) ||
                    sameShipmentSelected
                  }
                  disabledReason={
                    sameShipmentSelected
                      ? translate('app.sales.pack.alreadySelected')
                      : ''
                  }
                  onCheckChange={(e) => handleChange(e, sale)}
                  expanded={!!expandedRows[index]}
                  onToggleExpand={() => toggleExpandRow(index)}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={data.total ? data.total : data.pages * recordsPerPage}
        page={currentPage - 1}
        rowsPerPageOptions={[]}
        rowsPerPage={recordsPerPage}
        onChangePage={(event, newPage) => handleChangePage(event, newPage + 1)}
      />
    </>
  );
};

function areEqual(prevProps, nextProps) {
  return prevProps.data === nextProps.data;
}

export default withStyles(styles)(React.memo(SalesTable, areEqual));
